import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageProvider';
import { useThemeContext } from '../context/ThemeProvider';
import { devLog } from '../utils/devLog';

// 节奏类型分类配置
interface RhythmCategory {
  key: string;
  nameKey: string;
  icon: string;
  patterns: string[];
}

interface DrumPattern {
  key: string;
  nameKey: string;
  tempo: number;
}

const RHYTHM_CATEGORIES: Record<string, RhythmCategory> = {
  rock: {
    key: 'rock',
    nameKey: 'rhythmCategories.rock',
    icon: '🎸',
    patterns: ['rock', 'rockBallad', 'rockShuffle', 'breakbeat', 'progressive', 'reggae']
  },
  electronic: {
    key: 'electronic', 
    nameKey: 'rhythmCategories.electronic',
    icon: '🔊',
    patterns: ['disco', 'house', 'techno', 'trap', 'dnb']
  },
  jazz: {
    key: 'jazz',
    nameKey: 'rhythmCategories.jazz', 
    icon: '🎷',
    patterns: ['jazz', 'jazzWaltz', 'bossa', 'shuffle']
  },
  funk: {
    key: 'funk',
    nameKey: 'rhythmCategories.funk',
    icon: '🕺',
    patterns: ['funk', 'funkGhost', 'neosoul']
  },
  world: {
    key: 'world',
    nameKey: 'rhythmCategories.world',
    icon: '🌍', 
    patterns: ['latin', 'salsa', 'samba', 'afrobeat']
  },
  chillhop: {
    key: 'chillhop',
    nameKey: 'rhythmCategories.chillhop',
    icon: '🎶',
    patterns: ['chillhopGroove1', 'chillhopGroove2', 'chillhopGroove3']
  }
};

interface PatternSelectorProps {
  selectedPattern: string;
  onPatternChange: (patternKey: string) => void;
  allPatterns: DrumPattern[];
}

export default function PatternSelector({
  selectedPattern,
  onPatternChange,
  allPatterns
}: PatternSelectorProps): JSX.Element {
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [availablePatterns, setAvailablePatterns] = useState<DrumPattern[]>([]);
  const { t } = useLanguage();
  const { getClass } = useThemeContext();
  const getThemeClass = getClass;

  // 根據selectedPattern找到對應的category
  const findCategoryForPattern = (patternKey: string): string | null => {
    if (!patternKey) return null;
    
    for (const categoryKey in RHYTHM_CATEGORIES) {
      const category = RHYTHM_CATEGORIES[categoryKey];
      if (category.patterns.includes(patternKey)) {
        return categoryKey;
      }
    }
    return null;
  };

  // 監聽selectedPattern的變化，自動同步category和availablePatterns
  useEffect(() => {
    if (selectedPattern) {
      const categoryKey = findCategoryForPattern(selectedPattern);
      if (categoryKey && categoryKey !== selectedCategory) {
        devLog(`🔄 PatternSelector: Auto-selecting category ${categoryKey} for pattern ${selectedPattern}`);
        setSelectedCategory(categoryKey);
        
        const category = RHYTHM_CATEGORIES[categoryKey];
        const patterns = allPatterns.filter(pattern => 
          category.patterns.includes(pattern.key)
        );
        setAvailablePatterns(patterns);
      }
    }
    // 移除自動清空category的邏輯，避免在pattern清空時誤清空category
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPattern, allPatterns]); // 故意省略selectedCategory依賴，避免循環

  // 處理重置選擇
  const resetSelection = (): void => {
    setSelectedCategory('');
    setAvailablePatterns([]);
    onPatternChange('');
  };

  // 处理类型选择
  const handleCategoryChange = (categoryKey: string): void => {
    if (!categoryKey) {
      // 選擇空值時，完全重置
      resetSelection();
      return;
    }
    
    setSelectedCategory(categoryKey);
    
    const category = RHYTHM_CATEGORIES[categoryKey];
    const patterns = allPatterns.filter(pattern => 
      category.patterns.includes(pattern.key)
    );
    setAvailablePatterns(patterns);
    
    // 只有在切換到不同的category時才清除pattern選擇
    // 如果重複選擇同一個category，保持現有的pattern選擇
    if (selectedPattern) {
      const currentPatternCategory = findCategoryForPattern(selectedPattern);
      if (currentPatternCategory !== categoryKey) {
        onPatternChange('');
      }
    }
  };

  // 处理模式选择
  const handlePatternChange = (patternKey: string): void => {
    onPatternChange(patternKey);
  };

  return (
    <div className="flex flex-col space-y-2">
      <label className="text-sm font-medium flex items-center space-x-1">
        <span>🎵</span>
        <span>{t('patternSelector')}:</span>
      </label>
      
      <div className="flex space-x-2">
        {/* 节奏类型选择器 */}
        <select
          value={selectedCategory}
          onChange={(e) => handleCategoryChange(e.target.value)}
          className={`border px-3 py-2 rounded-lg min-w-32 ${getThemeClass('border')} ${getThemeClass('select')}`}
        >
          <option value="">{t('selectRhythmType') || '選擇節奏類型...'}</option>
          {Object.values(RHYTHM_CATEGORIES).map(category => (
            <option key={category.key} value={category.key}>
              {category.icon} {t(category.nameKey)}
            </option>
          ))}
        </select>

        {/* 具体模式选择器 */}
        <select
          value={selectedPattern}
          onChange={(e) => handlePatternChange(e.target.value)}
          disabled={!selectedCategory}
          className={`border px-3 py-2 rounded-lg min-w-48 ${getThemeClass('border')} ${getThemeClass('select')} ${
            !selectedCategory ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          <option value="">{t('selectPattern') || '選擇具體模式...'}</option>
          {availablePatterns.map(pattern => (
            <option key={pattern.key} value={pattern.key}>
              {t(pattern.nameKey)} ({pattern.tempo} BPM)
            </option>
          ))}
        </select>
      </div>
      
      {/* 显示当前选择的信息 */}
      {selectedCategory && (
        <div className="text-xs text-gray-500 flex items-center space-x-1">
          <span>{RHYTHM_CATEGORIES[selectedCategory].icon}</span>
          <span>{t(RHYTHM_CATEGORIES[selectedCategory].nameKey)}</span>
          {selectedPattern && (
            <>
              <span>→</span>
              <span>{t(`drumPatterns.${selectedPattern}`)}</span>
            </>
          )}
        </div>
      )}
    </div>
  );
} 